import React from 'react';

import {
	AI_MENU_SECTION,
	MAIN_BLOCK_MENU_SECTION_RANK,
	AI_MENU_SECTION_RANK,
} from '@atlaskit/editor-common/block-menu';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RegisterBlockMenuComponent } from '@atlaskit/editor-plugin-block-menu';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarDropdownItemSection } from '@atlaskit/editor-toolbar';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { AIPlugin } from '../../aiPluginType';
import { countWords } from '../../pm-plugins/ai-monitor-watchmen/utils';
import type { EditorPluginAIProvider } from '../../types/types';
import { getBlockMenuActions } from './actions';
import { BlockMenuActionItem } from './BlockMenuActionItem';
import type { BlockMenuActionDef, BlockMenuSelectionContext } from './types';

/**
 * Builds the selection context from the current selection or the block that triggered the menu.
 *
 * When a preserved selection exists (e.g., the user selected multiple blocks before
 * opening the menu), the context aggregates text and block types across all selected
 * top-level blocks.
 *
 * Otherwise, falls back to the single block at `menuTriggerByNode.pos`.
 *
 * `getSelectionContext` is invoked once per action, and each action's `isHidden`/
 * `shouldShowInPrimary` predicate can run several times during a single block-menu
 * render pass (the renderer probes `isHidden` for the parent section as well as each
 * item). To avoid recomputing the potentially expensive `getTextInfo` doc walk for the
 * same selection on every call, the result is memoized against the current editor
 * `state` identity via `cache`. The cache is scoped to a single
 * `createAIBlockMenuComponents` call and is invalidated automatically whenever the
 * editor state changes (a new `state` object is produced by every transaction).
 */
const getSelectionContext = ({
	api,
	getEditorView,
	cache,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	cache?: SelectionContextCache;
	getEditorView: () => EditorView | undefined;
}): BlockMenuSelectionContext | null => {
	const editorView = getEditorView();

	if (!editorView) {
		return null;
	}

	const { state } = editorView;

	// Return the memoized context when it was computed for the current editor state.
	if (cache && cache.state === state) {
		return cache.context;
	}

	const context = computeSelectionContext({ api, editorView });

	// Note: `isHidden`/`shouldShowInPrimary` are invoked synchronously during
	// `BlockMenuComponent`'s render (via `willComponentRender`), so this write
	// technically happens during React's render phase. It is safe as a read-through
	// memo: the cache is keyed on the immutable editor `state` identity, so for a
	// given `state` the computed value is deterministic and re-computing/re-writing
	// it produces the exact same value with no externally-observable side effect.
	// This means React Strict Mode's double-invoke is a no-op here (the second call
	// hits the cache and returns the identical value). We intentionally cache at
	// this level — rather than lifting into component state/`useMemo` — because the
	// same value must be shared across many non-component predicate calls within a
	// single render pass, and `state` identity is the natural, always-correct key.
	if (cache) {
		cache.state = state;
		cache.context = context;
	}

	return context;
};

/**
 * Memoization cache for {@link getSelectionContext}. Keyed by the editor `state`
 * identity so the cached value is transparently invalidated when the state changes.
 */
type SelectionContextCache = {
	context: BlockMenuSelectionContext | null;
	state: EditorState | null;
};

const computeSelectionContext = ({
	api,
	editorView,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	editorView: EditorView;
}): BlockMenuSelectionContext | null => {
	const { state } = editorView;

	if (expValEquals('platform_editor_remix_in_block_menu', 'isEnabled', true)) {
		const textInfo = api?.blockControls?.actions.getTextInfo(editorView) ?? null;

		if (!textInfo) {
			return null;
		}

		return {
			textLength: textInfo.textLength,
			wordCount: countWords(textInfo.textContent),
		};
	}

	// Original inline logic (gate off)
	const blockControlsState = api?.blockControls?.sharedState.currentState();
	const preservedSelection = blockControlsState?.preservedSelection;

	// When a multi-block selection exists, aggregate text metrics across all selected blocks
	if (preservedSelection && preservedSelection.from !== preservedSelection.to) {
		const { from, to } = preservedSelection;
		const textContent = state.doc.textBetween(from, to, '\n');

		return {
			textLength: textContent.length,
			wordCount: countWords(textContent),
		};
	}

	// Single-block fallback: use the node that triggered the menu
	const pos = blockControlsState?.menuTriggerByNode?.pos;
	if (pos === null || pos === undefined) {
		return null;
	}

	const node = state.doc.nodeAt(pos);
	if (!node) {
		return null;
	}

	return {
		textLength: node.textContent.length,
		wordCount: countWords(node.textContent),
	};
};

/**
 * Determines whether an action should appear in the primary menu by
 * evaluating the action's `shouldShowInPrimary` predicate against the
 * current selection context.
 *
 * Falls back to `true` (show in primary) when selection context is unavailable.
 */
const shouldShowInPrimary = ({
	action,
	api,
	getEditorView,
	cache,
}: {
	action: BlockMenuActionDef;
	api: ExtractInjectionAPI<AIPlugin>;
	cache?: SelectionContextCache;
	getEditorView: () => EditorView | undefined;
}): boolean => {
	const context = getSelectionContext({ api, getEditorView, cache });
	if (!context) {
		return true;
	}
	return action.shouldShowInPrimary(context);
};

/**
 * Section wrapper for the AI block-menu group. Defined at module scope so it keeps a
 * stable component identity across renders/registrations, allowing React to reconcile
 * (update) rather than remount the section.
 */
const AIBlockMenuSection = ({ children }: { children: React.ReactNode }) => (
	<ToolbarDropdownItemSection hasSeparator={true}>{children}</ToolbarDropdownItemSection>
);
AIBlockMenuSection.displayName = 'AIBlockMenuSection';

export const createAIBlockMenuComponents = ({
	editorPluginAIProvider,
	getEditorView,
	api,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	editorPluginAIProvider: EditorPluginAIProvider;
	getEditorView: () => EditorView | undefined;
}): RegisterBlockMenuComponent[] => {
	const actions = getBlockMenuActions({
		editorPluginAIProvider,
	});

	// Shared, per-editor-state selection-context cache. All AI items' `isHidden`
	// predicates read from the same cache within a render pass, so the potentially
	// expensive `getTextInfo` doc walk runs at most once per editor state instead of
	// once per item (× the extra probes the renderer performs). The cache invalidates
	// itself automatically when the editor state changes.
	const selectionContextCache: SelectionContextCache = { state: null, context: null };

	return [
		// 1. AI section
		{
			type: 'block-menu-section',
			key: AI_MENU_SECTION.key,
			rank: (MAIN_BLOCK_MENU_SECTION_RANK as Record<string, number>)[AI_MENU_SECTION.key],
			component: AIBlockMenuSection,
		},
		// 2. Primary items — all actions, hidden when shouldShowInPrimary returns false
		...actions.map((action, index) => {
			// Create a stable component identity per action (once at registration) so React
			// can reconcile rather than remount the item on subsequent renders.
			const ActionComponent = () => (
				<BlockMenuActionItem
					action={action}
					api={api}
					editorPluginAIProvider={editorPluginAIProvider}
					getEditorView={getEditorView}
				/>
			);
			ActionComponent.displayName = `AIBlockMenuActionItem(${action.key})`;

			return {
				type: 'block-menu-item' as const,
				key: `ai-${action.key}`,
				parent: {
					type: 'block-menu-section' as const,
					key: AI_MENU_SECTION.key,
					rank: (AI_MENU_SECTION_RANK as Record<string, number>)[AI_MENU_SECTION.key],
				},
				rank: index,
				isHidden: () =>
					!shouldShowInPrimary({ action, api, getEditorView, cache: selectionContextCache }),
				component: ActionComponent,
			};
		}),
	];
};
